import { ReactElement, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';

import { PeopleListingFragment } from '@/__generated__/graphql';
import Hero, { CreateHeroProps } from '@/components/Hero';
import Filter from '@/components/MultipleSelectFilter';
import Pagination from '@/components/Pagination';
import PeopleCard from '@/components/PeopleCard';
import { ComponentProps } from '@components/ComponentProps';
import StyledPills from '@components/Pills';
import WYSIWYG from "@components/WYSIWYG";
import { GridContainer, mediaQueries } from '@theme/lib';

import PageMeta from "@components/PageMeta";
import { useMobile } from "@lib/hooks/useMobile";
import {
  applyFilters,
  changePage,
  formatFilters,
  getAppliedFilters,
  ListingGridContainer,
  removeFilterFromSelected,
  SelectedFilters,
  StyledCardWrapper,
  StyledFilterList,
  StyledFilterWrapper,
} from '@lib/listing';

const StyledPeopleListing = styled.div`
  padding-bottom: 4.5rem;
  ${mediaQueries.lg} {
    padding-bottom: 12rem;
  }

  li {
    list-style: none;
  }
`;

const StyledCardList = styled(ListingGridContainer).attrs({ as: 'ul' })`
  display: flex;
  justify-content: center;
  align-items: center;
  column-gap: 3rem;
  flex-wrap: wrap;

  ${mediaQueries.md} {
    display: grid;
    column-gap: none;
  }
`;

const StyledPeopleCardWrapper = styled(StyledCardWrapper)`
  flex-grow: 1;

  .people-card {
    max-width: none;
    min-width: 18rem;
  }
`;

type PeopleListingProps = PeopleListingFragment & {
  pageUrl: string;
};

export default function PeopleListing({
  pageUrl,
  hero,
  content,
  searchFilters,
  searchResults,
  metatags,
}: ComponentProps<PeopleListingProps>): ReactElement<any> {
  const heroProps = hero ? CreateHeroProps(hero) : null;
  const isMobile = useMobile(1200);
  const resultSection = useRef<HTMLDivElement|null>(null);

  // Applied filters are the filters currently applied in the url. Only recompute this value if searchFilters change
  const appliedFilters = useMemo(
    () =>
      searchFilters
        ? getAppliedFilters(pageUrl, searchFilters.map((searchFilter) => searchFilter.name))
        : {},
    [searchFilters, pageUrl]
  );

  // Selected filters are the filters the user has selected but not yet applied
  const [selectedFilters, setSelectedFilters] = useState(appliedFilters);
  const [pills, setPills] = useState(Object.entries(appliedFilters).map(([key, value], index) => [...value]).flat());
  const [filtersCleared, setFiltersCleared] = useState(() => false);
  useEffect(() => {
    setFiltersCleared(localStorage.getItem("filtersCleared") === 'true' ? true : false);
  }, []);

  const onCheckboxChange = (filterType: string, value: string) => {
    setSelectedFilters((prev) => {
      // Make a deep copy of the previous selectedFilters state
      const newFilters: SelectedFilters = {};
      for (const key in prev) {
        newFilters[key] = new Set(prev[key]);
      }

      // Toggle the changed value
      if (newFilters[filterType].has(value)) {
        newFilters[filterType].delete(value);
      } else {
        newFilters[filterType].add(value);
      }

      return newFilters;
    });
  };

  // When the user close the dropdown for a filter type, reset the selected filters of that filter type to what's currently applied
  const resetSelection = (filterType: string) => {
    setSelectedFilters((prev) => {
      // Make a deep copy of the previous selectedFilters state
      const newFilters: SelectedFilters = {};
      for (const key in prev) {
        newFilters[key] = new Set(prev[key]);
      }

      // Reset the filters of this filter type
      newFilters[filterType] = new Set(appliedFilters[filterType]);
      return newFilters;
    });
  };

  const removeFilter = async (filter: string) => {
    localStorage.setItem("filtersCleared", "true");
    applyFilters(pageUrl, removeFilterFromSelected(selectedFilters, filter))
  }

  const formattedFilters = formatFilters({
    href: pageUrl,
    filters: searchFilters ?? [],
    selectedFilters,
    appliedFilters,
    onCheckboxChange,
    onApply: applyFilters,
    onClose: resetSelection,
    title: 'Filter by department',
  });

  useEffect(() => {
    if (Object.keys(appliedFilters).length > 0 && appliedFilters?.department?.size > 0 || filtersCleared === true) {
      if (resultSection.current !== null) {
        let headerOffset = isMobile ? 110 : 180;
        let elementPosition = resultSection.current.getBoundingClientRect().top;
        let offsetPosition = elementPosition + window.pageYOffset - headerOffset;
        window.scrollTo({
          top: offsetPosition,
          behavior: "smooth"
        });
      }
    }

    if (filtersCleared === true) {
      localStorage.removeItem('filtersCleared');
    }
  }, [appliedFilters, isMobile, filtersCleared]);

  return (
    <>
      <PageMeta
        metatags={metatags}
        breadcrumbs={[]}
        path={pageUrl}
      />
      <StyledPeopleListing>
        {heroProps && <Hero {...heroProps} />}
        {content && <GridContainer><WYSIWYG id="people-content" {...content} /></GridContainer>}
        <ListingGridContainer ref={resultSection}>
          <StyledFilterWrapper>
            {formattedFilters.length === 1 && <Filter {...formattedFilters[0]} />}
            {formattedFilters.length > 1 && (
              <StyledFilterList>
                {formattedFilters.map((filter) => (
                  <li key={filter.title}>
                    <Filter {...filter} />
                  </li>
                ))}
              </StyledFilterList>
            )}
          </StyledFilterWrapper>
          {pills.length > 0 && <StyledPills items={pills} onClick={removeFilter} />}
        </ListingGridContainer>
        {searchResults && (
          <>
            {searchResults.resultList && (
              <StyledCardList>
                {searchResults.resultList.map((result) => (
                  <StyledPeopleCardWrapper key={result.id}>
                    <PeopleCard
                      url={result.path}
                      name={result.title}
                      jobTitle={result.jobTitle ?? ''}
                      image={result.teaserImage}
                    />
                  </StyledPeopleCardWrapper>
                ))}
              </StyledCardList>
            )}
            <Pagination
              href={pageUrl}
              totalPages={
                searchResults.totalPages ?? Math.ceil(searchResults.total / searchResults.pageSize)
              }
              currentPage={Math.max(searchResults.page + 1, 1)}
              siblingCount={3}
              onPageChange={changePage}
            />
          </>
        )}
      </StyledPeopleListing>
    </>
  );
}
